import type { Patient, Task } from 'fhir/r4';
import {
  AUTOMATED_TASK_SUMMARY,
  EMAIL_TEMPLATE_CONTENT,
  getReferralPartnerSummaryTemplate,
  isReferralPartnerSummaryTemplate,
  getStateSummaryTemplate,
} from 'fhir-sdoh/common/constants';
import moment from 'moment-timezone';
import { getPatientExtensionCodingDisplay } from 'utils/FHIRUtils';
import * as Helpers from 'utils/Helpers';
import { mapKey } from 'utils/mapKey';

import { TASK_BORDER } from './fixtures';

/**
 * Parse a Task executionPeriod date string, correcting for the backend's
 * America/New_York wall-clock digits stored with a misleading `+00:00` offset
 * (required by the scheduler's Eastern-time FHIR search).
 *
 * - Date-only values (`YYYY-MM-DD`) pass through unchanged.
 * - Datetime values have the offset stripped and are interpreted as Eastern.
 */
export const parseTaskDueDate = (dateStr: string): moment.Moment => {
  const bare = dateStr.replace(/[+-]\d{2}:\d{2}$|Z$/, '');
  if (!bare.includes('T')) {
    return moment(bare, 'YYYY-MM-DD');
  }
  return moment.tz(bare, 'America/New_York');
};

export const getVendorValue = (task: Task) => {
  return task.identifier?.find((id) => id.system?.includes('vendor-codes'))?.value ?? null;
};

export const getCreateOrUpdatedInfo = (task: Task) => {
  const createdDate = parseTaskDueDate(task.executionPeriod?.start ?? '').local().format('MM-DD-YYYY').toString();
  const updatedDate = moment(task.meta?.lastUpdated).local().format('MM-DD-YYYY').toString();

  if (new Date(createdDate) <= new Date(updatedDate) && task.requester) {
    return `(created by ${task.requester.display} on ${createdDate})`;
  }

  const updatedByName =
    task.identifier?.find((id) => id.system === 'https://projectwell.io/fhir/identifiers/last-modified-by')?.value ??
    task.owner?.display;
  return `(updated by ${updatedByName} on ${updatedDate})`;
};

export const getTaskBorderColor = (taskPayload: Task, taskDueDateStatus: string) => {
  const taskHelper = new Helpers.TaskHelper(taskPayload);
  const taskCode = taskHelper.getTaskCode()?.code;
  const taskStatus = taskHelper.getStatus();
  const taskStatusReason = taskHelper.getStatusReason();
  let taskBorder = '';

  const isPastOrToday = taskDueDateStatus === 'pastdue' || taskDueDateStatus === 'today';
  const isIncomplete = taskStatus !== 'completed' && taskStatus !== 'failed';

  // -Outreach task with outreach status AND any outreach reason selected (i.e VM): not overdue, without the red boarder
  // -Outreach task with outreach status WITHOUT outreach reason: leave it as overdue
  // -Outreach task with status other than outreach status AND due date as PAST = past due (anything for a future date no that runs as usual)
  if (taskCode === 'outreach') {
    if (taskStatus === 'received' && taskStatusReason) {
      taskBorder = TASK_BORDER['future']; // Outreach task with status reason is not showing as past due
    } else if (isIncomplete && isPastOrToday) {
      taskBorder = TASK_BORDER[taskDueDateStatus];
    }
  } else if (isIncomplete && isPastOrToday) {
    taskBorder = TASK_BORDER[taskDueDateStatus];
  }

  return taskBorder;
};

export const getTaskDueDateStatus = (task: Task) => {
  let duedate = '';
  let taskDueDateStatus = '';
  const status = task.status;

  if (task.executionPeriod) {
    duedate = parseTaskDueDate(task.executionPeriod.end ?? task.executionPeriod.start!).local().format('MM/DD/YYYY');
  }

  const today = moment().format('MM/DD/YYYY');
  if (moment(duedate).isSame(today)) {
    taskDueDateStatus = 'today';
  } else if (moment(duedate).isAfter(today)) {
    taskDueDateStatus = 'future';
  } else {
    taskDueDateStatus = 'pastdue';
  }

  return { duedate, status, taskDueDateStatus };
};

const normalizeDescription = (description: string): string => {
  // Patterns to match and normalize
  // 1. Automated Task: Send weekly check in survey 7 -> Automated Task: Send weekly check in survey
  if (/^Automated Task: Send weekly check in survey( \d+)?$/.test(description)) {
    return 'Automated Task: Send weekly check in survey';
  }
  // 2. Automated Task: Send final check in (\d+ )?survey -> Automated Task: Send final check in survey
  if (/^Automated Task: Send final check in( \d+)? survey$/.test(description)) {
    return 'Automated Task: Send final check in survey';
  }
  // 3. System check for completed web survey 'member-program-end-bcmn-msho-chro(-\d+)?', send follow up survey if not found
  const systemCheckRegex =
    /^System check for completed web survey 'member-program-end-bcmn-msho-chro(-\d+)?', send follow up survey if not found$/;
  if (systemCheckRegex.test(description)) {
    return "System check for completed web survey 'member-program-end-bcmn-msho-chro', send follow up survey if not found";
  }
  // Default: return as is
  return description;
};

export const openPreview = (task: Task, fhirPatient: Patient | undefined = undefined) => {
  if (!task.description) return null;

  const { description, basedOn } = task;
  const orgIndex = basedOn?.findIndex((obj) => obj.reference?.startsWith('Organization'));
  const org = orgIndex !== -1 && (orgIndex || orgIndex === 0) ? basedOn?.[orgIndex]?.reference?.split('/')?.[1] : '';

  // Normalize the description for template lookup
  const normalizedDescription = normalizeDescription(description);

  // Handle specific cases first (using normalizedDescription)
  if (
    normalizedDescription === 'Automated Task: Send Nutrition Tip Week' ||
    normalizedDescription === 'Automated Task: Send Resources Survey' ||
    normalizedDescription === 'System check for member food rating response, send communication if not found'
  ) {
    const emailTemplateID = AUTOMATED_TASK_SUMMARY[normalizedDescription] as string | undefined;
    return emailTemplateID ? EMAIL_TEMPLATE_CONTENT[emailTemplateID] : 'Sorry, no preview available for this email.';
  }

  // Otherwise, handle dynamic organization-based template lookup
  // `AUTOMATED_TASK_SUMMARY` mixes plain template ids with per-org arrays of
  // `{ description: templateId }`, so its inferred value type is unusable here. The
  // `Array.isArray` below is the code's existing discriminator, not one added here.
  const automatedTaskSummary = AUTOMATED_TASK_SUMMARY[mapKey(org)] as Record<string, string>[] | string | undefined;
  if (Array.isArray(automatedTaskSummary)) {
    const summary = automatedTaskSummary.find(
      (summary) => summary[normalizedDescription] !== undefined
    );
    if (summary) {
      const emailTemplateID = summary[normalizedDescription]!;
      if (emailTemplateID.includes(',')) {
        const referralPartner = getPatientExtensionCodingDisplay(fhirPatient, 'REFERRAL_PARTNER');
        const summaryTemplate = isReferralPartnerSummaryTemplate(emailTemplateID)
          ? getReferralPartnerSummaryTemplate(referralPartner, emailTemplateID)
          : getStateSummaryTemplate(fhirPatient?.address?.[0]?.state as string, emailTemplateID);
        return summaryTemplate || 'Sorry, no preview available for this email.';
      } else {
        const emailTemplateID = summary[normalizedDescription]!;
        return EMAIL_TEMPLATE_CONTENT[emailTemplateID] || 'Sorry, no preview available for this email.';
      }
    }
  }

  return 'Sorry, no preview available for this email.';
};

export const sortByPriorityAndDate = (a: Task, b: Task) => {
  const priorityMap: Record<string, number> = { routine: 1, urgent: 2, asap: 3, stat: 4 };
  const priorityA = priorityMap[a.priority ?? 'routine'];
  const priorityB = priorityMap[b.priority ?? 'routine'];

  const aDate = parseTaskDueDate(a.executionPeriod?.end ?? a.executionPeriod?.start ?? '').toDate();
  const bDate = parseTaskDueDate(b.executionPeriod?.end ?? b.executionPeriod?.start ?? '').toDate();

  return priorityB! - priorityA! || aDate.getTime() - bDate.getTime();
};

export const getTaskOrganization = (t: Task) => {
  return t?.basedOn?.find((obj) => obj?.['reference']?.startsWith('Organization'))?.reference?.split('/')?.[1];
};
